import { Spinner } from '@creditornot/cb-ingredients';
import React from 'react';
import { useRouteMatch } from 'react-router';
import { Redirect, Switch, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { PosIntegrationStatus, Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { RouteWhenAuthenticated } from 'common/components';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import { useExternalAccessRights, useVenuePosIntegrationStatus } from 'common/hooks';
import useCountryConfig from 'common/hooks/useCountryConfig';
import { RetailContextProvider } from 'common/hooks/useRetailContext';
import { isRetailVenue } from 'common/utils';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import { VenueUserManagement } from 'modules/access-control';
import { VenueBestOfWolt } from 'modules/best-of-wolt/feature/VenueBestOfWolt';
import BulkImageRouteRenderer from 'modules/bulk-image-upload/BulkImageUploadRenderer';
import VenueCampaignRoutes from 'modules/campaigns/VenueCampaignRoutes';
import { CountryConfigContextProvider } from 'modules/country/CountryConfigContext';
import { CountryConfig } from 'modules/country/types';
import FeatureVenueInsights from 'modules/insights-v2/feature/FeatureVenueInsights';
import { VenueMenuRoot } from 'modules/menu/components/VenueMenuRoot';
import { VenueNps } from 'modules/nps/features/VenueNps';
import PayoutReports from 'modules/payout-reports/PayoutReportPage';
import PickingRouteRoot from 'modules/picking-route';
import Purchases from 'modules/purchases/Purchases';
import Settlements from 'modules/settlements/Settlements';
import { SidebarNavigationVenueTemplate } from 'modules/sidebar/SidebarNavigationTemplate';
import { VenueTranslationRoot } from 'modules/translation/components/VenueTranslationRoot';
import { useVenueContext } from 'modules/venue/VenueContext';
import VenueDetails from 'modules/venue/components/VenueDetails';
import VenueRoot from 'modules/venue/components/VenueRoot';

import PageLoadError from '../PageLoadError';
import { useTelemetryVenueMetadata } from '../telemetry/hooks/metadata';

type Props = {
  countryConfig: CountryConfig;
  venue: Venue;
  posIntegrationStatus?: PosIntegrationStatus | null;
};

export const VenueRoutes = ({ countryConfig, venue, posIntegrationStatus }: Props) => {
  const { path, url: venueRootUrl } = useRouteMatch();
  const { search } = useLocation();
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  return (
    <RetailContextProvider venues={[venue]}>
      <SidebarNavigationVenueTemplate venue={venue}>
        <CountryConfigContextProvider value={countryConfig}>
          <CurrencyContext.Provider value={{ currency: venue.currency }}>
            <BulkImageRouteRenderer venue={venue}>
              {({ routeRenderProp }) => (
                <Switch>
                  <RouteWhenAuthenticated exact path={`${path}/`} component={VenueRoot} />
                  {permissionUtils.hasVenueInsightsPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated path={`${path}/insights`} component={FeatureVenueInsights} />
                  )}
                  {permissionUtils.hasVenueInsightsPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated path={`${path}/best-of-wolt`} component={VenueBestOfWolt} />
                  )}
                  {permissionUtils.hasVenueUserManagementPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated path={`${path}/access`} component={VenueUserManagement} />
                  )}
                  {permissionUtils.hasVenueDetailsViewPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated exact path={`${path}/details`} component={VenueDetails} />
                  )}
                  {permissionUtils.hasPurchasesPermission(permissions, externalAccessRights, venue) && (
                    <Redirect from={`${path}/live-purchases`} to={`${path}/purchases/this-week${search}`} />
                  )}
                  {permissionUtils.hasPurchasesPermission(permissions, externalAccessRights, venue) && (
                    <Redirect
                      from={`${path}/purchases-history/:period?`}
                      to={{ pathname: `${path}/purchases/:period?`, search }}
                    />
                  )}
                  {permissionUtils.hasPurchasesPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated path={`${path}/purchases`} component={Purchases} />
                  )}
                  {permissionUtils.hasSettlementsPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated path={`${path}/additions-and-deductions`} component={Settlements} />
                  )}

                  {permissionUtils.hasMenuEditorPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated path={`${path}/menu-editor`} component={VenueMenuRoot} />
                  )}
                  {permissionUtils.hasCampaignViewPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated path={`${path}/campaigns`} component={VenueCampaignRoutes} />
                  )}
                  {permissionUtils.hasPickingRoutePermission(permissions, externalAccessRights, venue) &&
                    isRetailVenue(venue) && (
                      <RouteWhenAuthenticated path={`${path}/picking-route`} component={PickingRouteRoot} />
                    )}
                  {permissionUtils.hasPayoutReportsPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated exact path={`${path}/payouts`} component={PayoutReports} />
                  )}
                  {permissionUtils.hasTranslationPermission(permissions, externalAccessRights, venue) && (
                    <RouteWhenAuthenticated exact path={`${path}/translation`} component={VenueTranslationRoot} />
                  )}
                  {permissionUtils.hasMenuContentEditPermission(
                    permissions,
                    externalAccessRights,
                    venue,
                    posIntegrationStatus,
                  ) && <RouteWhenAuthenticated exact path={`${path}/bulk-image-upload`} render={routeRenderProp} />}

                  {/* Redirect to venue root when accessing a route without permissions */}
                  <RouteWhenAuthenticated render={props => <Redirect {...props} to={venueRootUrl} />} />
                </Switch>
              )}
            </BulkImageRouteRenderer>
          </CurrencyContext.Provider>
        </CountryConfigContextProvider>
      </SidebarNavigationVenueTemplate>
    </RetailContextProvider>
  );
};

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

export default () => {
  const venue = useVenueContext();
  const { countryConfig, error: countryConfigError } = useCountryConfig(venue?.country ?? null);
  const { data: posIntegrationStatus, isFetched: hasPosIntegrationData } = useVenuePosIntegrationStatus(
    venue?.id || null,
  );
  useTelemetryVenueMetadata();
  const loadingCountryConfig = !countryConfig && !countryConfigError;

  if (loadingCountryConfig || !hasPosIntegrationData) {
    return (
      <SpinnerContainer>
        <Spinner size="large" style={{ position: 'fixed' }} />
      </SpinnerContainer>
    );
  }

  if (venue && countryConfig) {
    return (
      <>
        <VenueNps />
        <VenueRoutes countryConfig={countryConfig} venue={venue} posIntegrationStatus={posIntegrationStatus} />
      </>
    );
  }

  return <PageLoadError />;
};
